import * as React from "react";
import {ISchedule, IScheduleEntry} from "@app-types/websocket/schedule.types";
import {ITodo, RENEWAL, TODO_TYPE} from "@app-types/websocket/todo.types";
import SCHEDULE_TYPES from "@constants/scheduleTypes";
import {isSameDay} from "@helpers/DateHelper";

const HOUR = 3600 * 1000;
// Responsible people are reminded the last day; everyone else is told about unseen items three days ahead.
export const PACKING_RESPONSIBLE_WINDOW = 24 * HOUR;
export const PACKING_UNSEEN_WINDOW = 72 * HOUR;

export interface INextHandover {
  time?: number;
  atSchool: boolean;
  location?: string;
  dropOffParent?: number;
  pickUpParent?: number;
}

const startOfDay = (time: number) => {
  const date = new Date(time);
  date.setHours(0, 0, 0);
  return date.getTime();
};

const earliestEnd = (entries: {[key: string]: IScheduleEntry}, after: number) => {
  let next: IScheduleEntry | undefined;
  for (const key of Object.keys(entries)) {
    const entry = entries[key];
    if (entry.endTime > after && (next === undefined || entry.endTime < next.endTime)) {
      next = entry;
    }
  }
  return next;
};

export const findNextHandover = (schedule: ISchedule): INextHandover => {
  const previousHandoverMorning = startOfDay(schedule.lastHandover);
  const nextA = earliestEnd(schedule.data.scheduleEntriesParentA, previousHandoverMorning);
  const nextB = earliestEnd(schedule.data.scheduleEntriesParentB, previousHandoverMorning);
  const handover: INextHandover = {atSchool: false};
  if (!nextA || !nextB) {
    return handover;
  }

  const aFirst = nextA.endTime < nextB.endTime;
  const first = aFirst ? nextA : nextB;
  const second = aFirst ? nextB : nextA;
  handover.dropOffParent = aFirst ? schedule.data.parentA : schedule.data.parentB;
  handover.pickUpParent = aFirst ? schedule.data.parentB : schedule.data.parentA;

  if (isSameDay(new Date(first.endTime), new Date(second.startTime))) {
    handover.time = first.endTime;
    if (first.endAtSchool && second.startAtSchool) {
      handover.atSchool = true;
    } else if (first.endLoc === second.startLoc) {
      handover.location = first.endLoc;
    }
  }
  return handover;
};

// A handover stays current for the whole day it happens on.
export const handoverHasBeen = (handover: INextHandover, now: number) =>
  handover.time === undefined || handover.time < startOfDay(now);

// The items shown in a schedule's list, oldest first so the order stays put while items are edited or ticked
// off, with renewing items assigned to whoever drops off or picks up.
export const packingItemsForSchedule = (todos: ITodo[], handover: INextHandover, myAccountId: number): ITodo[] => {
  const items: ITodo[] = [];
  for (const todo of todos.slice().sort((a, b) => a.id - b.id)) {
    if (todo.hidden && todo.hidden.indexOf(myAccountId) > -1) {
      continue;
    }
    if (todo.renewal === RENEWAL.DROPPING_OFF && handover.dropOffParent !== undefined) {
      items.push({...todo, assigned: [handover.dropOffParent]});
    } else if (todo.renewal === RENEWAL.PICKING_UP && handover.pickUpParent !== undefined) {
      items.push({...todo, assigned: [handover.pickUpParent]});
    } else {
      items.push(todo);
    }
  }
  return items;
};

export interface IPackingGroup {
  // The child the items are tagged with; undefined for the general part.
  child?: number;
  items: ITodo[];
}

/**
 * With more than one child, and once any item is tagged with one of them, the list splits into a part
 * per child, in the schedule's order, followed by a general part. Otherwise it stays one list.
 */
export const groupPackingItems = (items: ITodo[], children: number[]): IPackingGroup[] => {
  const forChild = (todo: ITodo) => todo.tagged !== undefined && todo.tagged !== null
    && children.indexOf(todo.tagged) > -1;
  if (children.length < 2 || !items.some(forChild)) {
    return [{items}];
  }
  const groups: IPackingGroup[] = children.map(child => ({child, items: items.filter(todo => todo.tagged === child)}));
  groups.push({items: items.filter(todo => !forChild(todo))});
  return groups;
};

export const isPackingTodo = (todo: ITodo) => todo.type === TODO_TYPE.HANDOVER && todo.handovergroup === undefined;

const isActiveSchedule = (schedule: ISchedule) =>
  schedule.status !== SCHEDULE_TYPES.DELETED && schedule.status !== SCHEDULE_TYPES.DELETED_WIP;

export interface IPackingBadge {
  count: number;
  // When the count next changes just from time passing.
  nextChange?: number;
}

/**
 * An item counts when it is in a shown schedule, not hidden by me, and either
 * - not done, I am responsible and the handover is less than 24 hours away, or
 * - I have not seen it and the handover is at most 72 hours away.
 */
export const countPackingBadge = (todos: ITodo[], schedules: ISchedule[], calendars: number[] | undefined,
                                  myAccountId: number, now: number): IPackingBadge => {
  const badge: IPackingBadge = {count: 0};
  if (!calendars) {
    return badge;
  }
  const changeAt = (time: number) => {
    if (time > now && (badge.nextChange === undefined || time < badge.nextChange)) {
      badge.nextChange = time;
    }
  };

  for (const schedule of schedules) {
    if (calendars.indexOf(schedule.id) === -1 || !isActiveSchedule(schedule)) {
      continue;
    }
    const handover = findNextHandover(schedule);
    if (handover.time === undefined) {
      continue;
    }
    const handoverDayEnd = new Date(handover.time);
    handoverDayEnd.setHours(24, 0, 0, 0);
    changeAt(handover.time - PACKING_UNSEEN_WINDOW);
    changeAt(handover.time - PACKING_RESPONSIBLE_WINDOW + 1);
    changeAt(handoverDayEnd.getTime());
    if (handoverHasBeen(handover, now)) {
      continue;
    }

    const untilHandover = handover.time - now;
    const scheduleTodos = todos.filter(todo => isPackingTodo(todo) && todo.scheduleId === schedule.id);
    for (const item of packingItemsForSchedule(scheduleTodos, handover, myAccountId)) {
      const responsible = item.completedBy === undefined && !!item.assigned
        && item.assigned.indexOf(myAccountId) > -1 && untilHandover < PACKING_RESPONSIBLE_WINDOW;
      const unseen = !(item.seen && item.seen.indexOf(myAccountId) > -1) && untilHandover <= PACKING_UNSEEN_WINDOW;
      if (responsible || unseen) {
        badge.count += 1;
      }
    }
  }
  return badge;
};

const MAX_TIMEOUT = 2147483647;

// Recounts when a 24/72 hour window opens or the handover day ends, and when the app comes back.
export const usePackingBadgeCount = (todos: ITodo[], schedules: ISchedule[], calendars: number[] | undefined,
                                     myAccountId: number): number => {
  const [now, setNow] = React.useState(Date.now);
  const badge = countPackingBadge(todos, schedules, calendars, myAccountId, now);

  React.useEffect(() => {
    const refresh = () => setNow(Date.now());
    const timer = badge.nextChange === undefined ? undefined
      : window.setTimeout(refresh, Math.min(MAX_TIMEOUT, Math.max(0, badge.nextChange - Date.now()) + 10));
    window.addEventListener('focus', refresh);
    document.addEventListener('visibilitychange', refresh);
    return () => {
      window.clearTimeout(timer);
      window.removeEventListener('focus', refresh);
      document.removeEventListener('visibilitychange', refresh);
    };
  }, [badge.nextChange]);

  return badge.count;
};



// WEBPACK FOOTER //
// ./src/components/next-handover/packingList.ts